'use client';

import { useState } from 'react';
import Link from 'next/link';

import { cn } from '@/lib/utils';
import { siteConfig } from '@/config/site';
import { Button } from '@/components/ui';

interface SeoBlockProps {
  className?: string;
}

export function SeoBlock({ className }: SeoBlockProps) {
  const { city } = siteConfig;
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className={cn('relative', className)}>
      <div className="relative">
        <div
          className={cn(
            'overflow-hidden transition-[max-height] duration-1000 ease-[cubic-bezier(0.32,0.72,0,1)]',
            isExpanded ? 'max-h-[1000px]' : 'max-h-[100px] sm:max-h-[110px]',
          )}
        >
          <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-[var(--text-primary)] mb-4">
            Доставка роллов и суши в {city.nameGenitive} — {siteConfig.name}
          </h2>

          <div className="text-sm sm:text-base leading-relaxed space-y-3 text-[var(--text-secondary)]">
            <p>
              <strong>{siteConfig.name}</strong> — это быстрая{' '}
              <strong>доставка роллов в {city.nameGenitive}</strong> прямо к вашей двери.
              Заказать роллы, суши, сеты и блюда японской кухни можно онлайн на сайте
              или в нашем приложении. Среднее время доставки — {siteConfig.deliveryTime.min}–{siteConfig.deliveryTime.max} минут,
              а в некоторые районы мы успеваем за 30 минут.
            </p>
            <p>
              В нашем меню — <strong>запечённые роллы</strong>, <strong>холодные роллы</strong>,{' '}
              <strong>темпура</strong>, классическая <strong>филадельфия</strong>,{' '}
              авторские сеты и многое другое. Все блюда готовятся из свежих ингредиентов
              с соблюдением традиций японской кухни. Мы работаем ежедневно
              с {siteConfig.workingHours.start}:00 до {siteConfig.workingHours.end}:00.
            </p>
            <p>
              Мы доставляем по всему {city.name} и ближайшим пригородам:{' '}
              {city.districts.slice(0, 4).map((d, i) => (
                <span key={d.slug}>
                  {i > 0 && ', '}
                  <span className="text-[var(--primary)]">{d.name}</span>
                </span>
              ))}
              {' '}и{' '}
              <Link href="/delivery#zones" className="text-[var(--primary)] hover:underline">
                другие районы
              </Link>.
              Минимальная сумма заказа — {siteConfig.minOrderAmount} ₽.
            </p>
            <p>
              Не знаете, <strong>где заказать роллы в {city.nameGenitive}</strong>?
              Почитайте{' '}
              <Link href="/reviews" className="text-[var(--primary)] hover:underline">
                отзывы наших клиентов
              </Link>
              {' '}или загляните в{' '}
              <Link href="/faq" className="text-[var(--primary)] hover:underline">
                частые вопросы
              </Link>.
              {' '}<strong>Вкусные роллы с доставкой на дом</strong> — это просто и быстро
              с {siteConfig.name}!
            </p>
          </div>
        </div>

        {/* Gradient — big, covers 60% of collapsed height */}
        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 pointer-events-none',
            'transition-opacity duration-700 ease-[cubic-bezier(0.32,0.72,0,1)]',
            isExpanded ? 'opacity-0' : 'opacity-100',
          )}
          style={{
            height: '120px',
            background: 'linear-gradient(to top, #F9F6FF 0%, #F9F6FF 40%, rgba(249,246,255,0.97) 60%, rgba(249,246,255,0.7) 80%, transparent 100%)',
          }}
        />
      </div>

      <div className="flex justify-center mt-4">
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setIsExpanded((v) => !v)}
          className="rounded-full"
        >
          {isExpanded ? 'Свернуть' : 'Показать полностью'}
        </Button>
      </div>
    </section>
  );
}
